// iter618 — Monthly Reconciliation Tracker page.
//
// Single-glance "Is this month closed?" view. Shows the past 12
// months as a list of cards, each with:
//   - bank-statement / journal-entry / settlement counts
//   - status pill (ok / review / missing)
//   - "Download auditor pack" button → calls /api/monthly-recon/auditor-pack
//     and triggers a JSON file download for the period.

import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileDown, CalendarCheck2, AlertTriangle, CheckCircle2, Download, Lock, Unlock, Share2, Copy, CalendarRange, Globe2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const STATUS_META = {
  ok:      { label: 'Open',       icon: CheckCircle2,    cls: 'bg-sky-100 text-sky-800 border-sky-300' },
  closed:  { label: 'Locked',     icon: Lock,            cls: 'bg-emerald-200 text-emerald-900 border-emerald-400' },
  review:  { label: 'Needs review', icon: AlertTriangle, cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  missing: { label: 'Missing data', icon: AlertTriangle, cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

export default function MonthlyReconciliationTrackerPage() {
  const [data, setData] = useState({ months: [] });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  // iter619 — actions per-month: close lock, share-with-auditor, format dropdown.
  const [closing, setClosing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [shareDialog, setShareDialog] = useState(null);
  // iter621 — Year-pack + side-by-side currency report state.
  const [yearPackBusy, setYearPackBusy] = useState(false);
  const [sideBySide, setSideBySide] = useState(null); // {month, period, data}
  const [sideBySideBusy, setSideBySideBusy] = useState(null); // {month, url, expires_at}

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/monthly-recon/tracker', { params: { months: 12 } });
      setData(data || { months: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tracker');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const downloadPack = async (period_start, format = 'json') => {
    try {
      setDownloading(`${period_start}-${format}`);
      const month = period_start.slice(0, 7);
      if (format === 'json') {
        const { data: pack } = await api.get('/monthly-recon/auditor-pack', { params: { month } });
        const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' });
        triggerBlobDownload(blob, `auditor-pack-${month}.json`);
      } else {
        // Excel + CSV come back as binary streams.
        const r = await api.get('/monthly-recon/auditor-pack', { params: { month, format }, responseType: 'blob' });
        const ext = format === 'csv' ? 'zip' : 'xlsx';
        triggerBlobDownload(r.data, `auditor-pack-${month}.${ext}`);
      }
      toast.success(`Auditor pack for ${month} downloaded (${format.toUpperCase()}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build auditor pack');
    } finally {
      setDownloading(null);
    }
  };

  const triggerBlobDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  // iter619 — Lock a month so journal-entry writes inside that range
  // are refused (returns HTTP 423). Idempotent.
  const closeMonth = async (m) => {
    const notes = window.prompt(`Mark ${m.period} as closed?\n\nOnce closed, the month is locked — journal entries dated in this range can't be edited or deleted until an admin reopens it. Optional note for the audit trail:`);
    if (notes === null) return;
    try {
      setClosing(m.period_start);
      const month = m.period_start.slice(0, 7);
      await api.post('/monthly-recon/close', { month, notes: notes.trim() || null });
      toast.success(`${m.period} marked as closed.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to close month');
    } finally {
      setClosing(null);
    }
  };

  const reopenMonth = async (m) => {
    if (!window.confirm(`Reopen ${m.period}? This restores edit access for journal entries in this range.`)) return;
    try {
      setClosing(m.period_start);
      const month = m.period_start.slice(0, 7);
      await api.post('/monthly-recon/reopen', { month });
      toast.success(`${m.period} reopened.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reopen month');
    } finally {
      setClosing(null);
    }
  };

  // iter619 — Generate a 7-day signed URL for the external auditor.
  const shareWithAuditor = async (m) => {
    try {
      setSharing(m.period_start);
      const month = m.period_start.slice(0, 7);
      const { data } = await api.post('/monthly-recon/share', { month, days: 7 });
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const url = `${base}${data.view_path}`;
      setShareDialog({ month, url, expires_at: data.expires_at, period: m.period });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSharing(null);
    }
  };

  const copyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareDialog.url);
      toast.success('Share link copied — paste into an email to your auditor.');
    } catch {
      toast.error('Could not copy. Manually select & copy the link.');
    }
  };

  // iter621 — Download one Excel workbook containing all 12 months.
  const downloadYearPack = async () => {
    const year = new Date().getFullYear();
    const explicit = window.prompt('Year-end pack — which year?', String(year));
    if (!explicit) return;
    const y = parseInt(explicit, 10);
    if (!y || y < 2000 || y > 2999) { toast.error('Invalid year.'); return; }
    try {
      setYearPackBusy(true);
      const r = await api.get('/monthly-recon/year-pack', { params: { year: y, format: 'excel' }, responseType: 'blob' });
      triggerBlobDownload(r.data, `year-pack-${y}.xlsx`);
      toast.success(`Year pack ${y} downloaded.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build year pack');
    } finally { setYearPackBusy(false); }
  };

  // iter621 — Side-by-side currency report for a single month.
  const showSideBySide = async (m) => {
    try {
      setSideBySideBusy(m.period_start);
      const month = m.period_start.slice(0, 7);
      const { data } = await api.get('/monthly-recon/side-by-side-currencies', { params: { month } });
      setSideBySide({ month, period: m.period, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build side-by-side report');
    } finally { setSideBySideBusy(null); }
  };

  const fmt = (n) => Number(n || 0).toLocaleString();

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="monthly-recon-tracker">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight flex items-center gap-2">
              <CalendarCheck2 size={24} className="text-emerald-600" />
              Monthly Reconciliation Tracker
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Past 12 months at a glance. Click any month to download the auditor pack — a single JSON bundle with journals, statements, balances, and the closeout digest.
            </p>
          </div>
          <Button variant="outline" onClick={fetchData} className="rounded-full" data-testid="tracker-refresh-btn">
            Refresh
          </Button>
          {/* iter621 — Year-end pack download. */}
          <Button
            variant="outline"
            onClick={downloadYearPack}
            disabled={yearPackBusy}
            className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="year-pack-btn"
          >
            {yearPackBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <CalendarRange size={14} className="mr-1" />}
            Year-end pack
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading 12 months…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tracker-months-grid">
            {data.months.map((m) => {
              const meta = STATUS_META[m.status] || STATUS_META.review;
              const StatusIcon = meta.icon;
              return (
                <Card key={`${m.year}-${m.month}`} className="rounded-2xl border border-border/60" data-testid={`tracker-month-${m.year}-${m.month}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-lg font-bold font-outfit truncate">{m.period}</div>
                        <div className="text-[11px] text-muted-foreground">{m.period_start} → {m.period_end}</div>
                      </div>
                      <Badge className={`shrink-0 ${meta.cls} border`}>
                        <StatusIcon size={11} className="mr-1" />{meta.label}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-[11px] pt-1 border-t border-border/40">
                      <div>
                        <div className="text-muted-foreground">Statements</div>
                        <div className="font-bold">{fmt(m.bank_statements)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Journals</div>
                        <div className="font-bold">{fmt(m.journal_entries)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Settlements</div>
                        <div className="font-bold">{fmt(m.settlements_count)}</div>
                      </div>
                    </div>
                    {m.settlements_total > 0 && (
                      <div className="text-[11px] text-muted-foreground">
                        Inter-branch: <strong className="text-foreground">{cur()} {fmt(m.settlements_total)}</strong>
                      </div>
                    )}
                    {m.overdue_customers > 0 && (
                      <div className="text-[11px] text-amber-700">
                        {m.overdue_customers} customer(s) with outstanding credit
                      </div>
                    )}
                    {m.notes?.length > 0 && (
                      <div className="text-[10px] text-rose-700 italic">
                        {m.notes.join(' · ')}
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full rounded-lg mt-2 text-[12px]"
                      onClick={() => downloadPack(m.period_start, 'json')}
                      disabled={downloading === `${m.period_start}-json`}
                      data-testid={`auditor-pack-btn-${m.year}-${m.month}`}
                    >
                      {downloading === `${m.period_start}-json` ? (
                        <><Loader2 size={12} className="mr-1 animate-spin" /> Building pack…</>
                      ) : (
                        <><FileDown size={12} className="mr-1" />Download JSON</>
                      )}
                    </Button>
                    <div className="grid grid-cols-2 gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        className="rounded-lg text-[11px] h-8"
                        onClick={() => downloadPack(m.period_start, 'excel')}
                        disabled={downloading === `${m.period_start}-excel`}
                        data-testid={`auditor-pack-excel-${m.year}-${m.month}`}
                      >
                        {downloading === `${m.period_start}-excel` ? <Loader2 size={11} className="animate-spin" /> : <><FileDown size={11} className="mr-1" />Excel</>}
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="rounded-lg text-[11px] h-8"
                        onClick={() => downloadPack(m.period_start, 'csv')}
                        disabled={downloading === `${m.period_start}-csv`}
                        data-testid={`auditor-pack-csv-${m.year}-${m.month}`}
                      >
                        {downloading === `${m.period_start}-csv` ? <Loader2 size={11} className="animate-spin" /> : <><FileDown size={11} className="mr-1" />CSV (.zip)</>}
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 pt-1 border-t border-border/40">
                      <Button
                        size="sm" variant="outline"
                        className="rounded-lg text-[11px] h-8 border-sky-300 text-sky-700 hover:bg-sky-50"
                        onClick={() => shareWithAuditor(m)}
                        disabled={sharing === m.period_start}
                        data-testid={`auditor-share-btn-${m.year}-${m.month}`}
                      >
                        {sharing === m.period_start ? <Loader2 size={11} className="animate-spin" /> : <><Share2 size={11} className="mr-1" />Share w/ Auditor</>}
                      </Button>
                      {m.status === 'closed' ? (
                        <Button
                          size="sm" variant="outline"
                          className="rounded-lg text-[11px] h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
                          onClick={() => reopenMonth(m)}
                          disabled={closing === m.period_start}
                          data-testid={`reopen-month-${m.year}-${m.month}`}
                        >
                          {closing === m.period_start ? <Loader2 size={11} className="animate-spin" /> : <><Unlock size={11} className="mr-1" />Reopen</>}
                        </Button>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          className="rounded-lg text-[11px] h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => closeMonth(m)}
                          disabled={closing === m.period_start}
                          data-testid={`close-month-${m.year}-${m.month}`}
                        >
                          {closing === m.period_start ? <Loader2 size={11} className="animate-spin" /> : <><Lock size={11} className="mr-1" />Mark closed</>}
                        </Button>
                      )}
                    </div>
                    {m.closed_at && (
                      <div className="text-[10px] text-emerald-700 pt-1">
                        Closed by <strong>{m.closed_by}</strong> on {new Date(m.closed_at).toLocaleDateString()}
                        {m.close_notes && <div className="italic">"{m.close_notes}"</div>}
                      </div>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      className="w-full text-[11px] text-indigo-700 hover:bg-indigo-50 rounded-lg"
                      onClick={() => showSideBySide(m)}
                      disabled={sideBySideBusy === m.period_start}
                      data-testid={`side-by-side-${m.year}-${m.month}`}
                    >
                      {sideBySideBusy === m.period_start ? <Loader2 size={11} className="animate-spin mr-1" /> : <Globe2 size={11} className="mr-1" />}
                      Side-by-side currencies
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!loading && data.months.length === 0 && (
          <div className="text-center py-16 text-muted-foreground italic">No months to show yet.</div>
        )}

        <div className="bg-info/5 border border-info/20 rounded-xl p-3 text-[11px] text-info/90 flex items-start gap-2">
          <Download size={14} className="shrink-0 mt-0.5" />
          <span>
            <strong>What&apos;s in the auditor pack?</strong> A single bundle (JSON, Excel, or CSV)
            containing the month&apos;s journal entries (up to 5,000), all bank statements, the
            trial-balance snapshot, customer / supplier outstanding credit lists, inter-branch
            settlements, sales + expense totals, and (if available) the AI closeout digest.
            Use <strong>Share w/ Auditor</strong> to send a 7-day signed link instead — the
            auditor opens it in any browser, no login needed.
          </span>
        </div>

        {/* iter621 — Side-by-side currency dialog. */}
        <Dialog open={!!sideBySide} onOpenChange={(o) => { if (!o) setSideBySide(null); }}>
          <DialogContent className="max-w-3xl" data-testid="side-by-side-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-outfit">
                <Globe2 size={18} className="text-indigo-600" /> Currencies side-by-side · {sideBySide?.period}
              </DialogTitle>
              <p className="text-[12px] text-muted-foreground">
                Each branch in its own native currency, plus a roll-up in the tenant&apos;s parent currency
                (<strong>{sideBySide?.data?.parent_currency}</strong>). Rates default to 1:1 — wire in your FX layer for real conversions.
              </p>
            </DialogHeader>
            {sideBySide && (
              <div className="space-y-3">
                <div className="rounded-xl border border-border/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                      <thead className="bg-stone-50">
                        <tr className="text-left">
                          <th className="p-2">Branch</th>
                          <th className="p-2">Cur</th>
                          <th className="p-2 text-right">Sales (native)</th>
                          <th className="p-2 text-right">Expenses (native)</th>
                          <th className="p-2 text-right">Net (native)</th>
                          <th className="p-2 text-right">Net (parent)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sideBySide.data.rows.map((r) => (
                          <tr key={r.branch_id} className="border-t border-border/40">
                            <td className="p-2 font-medium">{r.branch_name}</td>
                            <td className="p-2"><span className="text-[10px] bg-stone-100 rounded px-1.5 py-0.5">{r.currency}</span></td>
                            <td className="p-2 text-right">{r.currency} {Number(r.sales).toLocaleString()}</td>
                            <td className="p-2 text-right">{r.currency} {Number(r.expenses).toLocaleString()}</td>
                            <td className={`p-2 text-right font-semibold ${r.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{r.currency} {Number(r.net).toLocaleString()}</td>
                            <td className={`p-2 text-right ${r.net_in_parent >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{sideBySide.data.parent_currency} {Number(r.net_in_parent).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* By-currency roll-up */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {sideBySide.data.by_currency.map((c) => (
                    <div key={c.currency} className="bg-indigo-50/40 border border-indigo-200 rounded-lg p-2.5 text-[12px]">
                      <div className="text-[10px] uppercase font-bold text-indigo-700 tracking-wider">{c.currency}</div>
                      <div>Sales: <strong>{c.sales.toLocaleString()}</strong></div>
                      <div>Expenses: <strong>{c.expenses.toLocaleString()}</strong></div>
                      <div className={c.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}>Net: <strong>{c.net.toLocaleString()}</strong></div>
                    </div>
                  ))}
                </div>
                {/* Parent total */}
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider opacity-80">Roll-up · {sideBySide.data.parent_currency}</div>
                    <div className="text-[13px] mt-0.5">Sales {sideBySide.data.parent_total.sales.toLocaleString()} · Expenses {sideBySide.data.parent_total.expenses.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] opacity-80">Net</div>
                    <div className="text-lg font-bold">{sideBySide.data.parent_currency} {sideBySide.data.parent_total.net.toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-[10px] text-amber-700 italic">{sideBySide.data.rate_disclaimer}</div>
                  {sideBySide.data.fx_rates_configured ? (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="fx-configured-badge">FX rates configured</span>
                  ) : (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid="fx-default-badge">Defaulting to 1:1 — configure FX rates</span>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* iter619 — Share-with-auditor result dialog. */}
        <Dialog open={!!shareDialog} onOpenChange={(o) => { if (!o) setShareDialog(null); }}>
          <DialogContent className="max-w-lg" data-testid="auditor-share-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-outfit">
                <Share2 size={18} className="text-sky-600" /> Auditor link ready
              </DialogTitle>
            </DialogHeader>
            {shareDialog && (
              <div className="space-y-3">
                <p className="text-[12px] text-muted-foreground">
                  Send this link to your external auditor. It opens the full <strong>{shareDialog.period}</strong> pack in any browser — no login required. Expires{' '}
                  <strong>{new Date(shareDialog.expires_at).toLocaleDateString()}</strong>.
                </p>
                <div className="flex items-center gap-2">
                  <Input value={shareDialog.url} readOnly className="font-mono text-[11px]" data-testid="auditor-share-url" onFocus={(e) => e.target.select()} />
                  <Button size="icon" variant="outline" onClick={copyShareUrl} title="Copy link" data-testid="auditor-share-copy">
                    <Copy size={14} />
                  </Button>
                </div>
                <div className="bg-amber-50/60 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-900">
                  <strong>Tip:</strong> the auditor can also append <code>?format=excel</code> or <code>?format=csv</code> to the URL to get a downloadable spreadsheet.
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShareDialog(null)} className="rounded-full">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
